import { gsap } from 'gsap';

/**
 * Mobile navigation menu functionality
 */
export default class MobileNavigation {
    constructor() {
        // Animation constants
        this.ANIMATION_CONFIG = {
            CONTAINER_DURATION: 1.0,
            CONTAINER_EASE: "back.out(1.4)",
            ITEM_DURATION: 0.3,
            ITEM_EASE: "back.out(1.2)",
            STAGGER_DELAY: 0.05,
            CLOSE_DURATION: 0.4,
            CLOSE_EASE: "power2.in"
        };

        this.mobileMenuContainer = document.querySelector('.menu-mobile');
        this.mobileMenuTrigger = document.querySelector('[data-mobile-menu-trigger]');
        this.mobileMenuDropdown = document.getElementById('mobile-menu-dropdown');
        this.mobileSearchTrigger = document.querySelector('[data-mobile-search-trigger]');
        this.mobileSearchDropdown = document.getElementById('mobile-search-menu');

        this.isMobileMenuOpen = false;
        this.isMobileSearchOpen = false;

        // Store original scroll position for restoration
        this.scrollPosition = 0;

        // Animation timeline
        this.menuTimeline = null;
        this.searchTimeline = null;
        this.searchCalculatedHeight = null;

        // Focus management
        this.focusableElements = null;
        this.firstFocusableElement = null;
        this.lastFocusableElement = null;

        // Resize handling
        this.resizeTimeout = null;
        this.roRafId = null;
        this.topBarResizeObserver = null;

        // Bound handlers for add/remove
        this.onHeaderRequestToggle = () => {
            // Allow natural height while logo size is transitioning
            if (!this.isMobileMenuOpen && this.mobileMenuContainer) {
                gsap.set(this.mobileMenuContainer, { height: 'auto' });
            }
        };
        this.onHeaderScrolledToggle = () => {
            // Recalculate and lock height after logo transition completes
            if (!this.isMobileMenuOpen) {
                // Use RAF to wait for layout to settle
                if (this.roRafId) cancelAnimationFrame(this.roRafId);
                this.roRafId = requestAnimationFrame(() => this.updateHeightOnResize());
            }
        };

        this.init();
    }

    waitForImagesAndSetHeight() {
        // Ensure all images in mobile header are loaded before calculating height
        const headerImages = this.mobileMenuContainer.querySelectorAll('img');
        const imagePromises = Array.from(headerImages).map(img => {
            return new Promise((resolve) => {
                if (img.complete) {
                    resolve();
                } else {
                    img.addEventListener('load', resolve);
                    img.addEventListener('error', resolve); // Also resolve on error to prevent hanging
                    // Fallback timeout to prevent indefinite waiting
                    setTimeout(resolve, 2000);
                }
            });
        });

        Promise.all(imagePromises).then(() => {
            // Add a small delay to ensure layout is stable and CSS is fully applied
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.setOriginalHeight();
                });
            });
        });
    }

    setOriginalHeight() {
        // Store original height for calculations
        const calculatedHeight = this.mobileMenuContainer.offsetHeight;

        // Defensive minimum height check (should be at least 60px for a typical mobile header)
        const minimumHeight = 60;
        this.originalHeight = Math.max(calculatedHeight, minimumHeight);

        // Set initial container state (just the top bar visible)
        gsap.set(this.mobileMenuContainer, {
            height: this.originalHeight
        });
    }

    init() {
        if (!this.mobileMenuContainer) return;

        this.bindEvents();
        this.setupSubmenus();
        this.setupFocusManagement();
        this.initializeGSAP();

        // Observe header top bar/logo size changes to keep container height in sync
        this.setupHeaderHeightObserver();
    }

    setupFocusManagement() {
        if (!this.mobileMenuDropdown) return;

        // Set up focusable elements query
        this.updateFocusableElements();

        // Handle dialog close on backdrop click or ESC key
        this.mobileMenuDropdown.addEventListener('cancel', (e) => {
            // Prevent default ESC behavior, we'll handle it ourselves
            e.preventDefault();
            this.closeMobileMenu();
        });

        // Handle focus trapping
        this.mobileMenuDropdown.addEventListener('keydown', (e) => {
            this.handleFocusTrap(e);
        });
    }

    updateFocusableElements() {
        if (!this.mobileMenuDropdown) return;

        const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        this.focusableElements = this.mobileMenuDropdown.querySelectorAll(focusableSelector);
        this.firstFocusableElement = this.focusableElements[0];
        this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
    }

    handleFocusTrap(e) {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
            // Shift + Tab
            if (document.activeElement === this.firstFocusableElement) {
                e.preventDefault();
                this.lastFocusableElement?.focus();
            }
        } else {
            // Tab
            if (document.activeElement === this.lastFocusableElement) {
                e.preventDefault();
                this.firstFocusableElement?.focus();
            }
        }
    }

    initializeGSAP() {
        // Set initial states for mobile menu container
        if (this.mobileMenuContainer) {
            // Wait for images to load before calculating height
            this.waitForImagesAndSetHeight();
        }

        // Set initial states for mobile menu dropdown
        if (this.mobileMenuDropdown) {
            gsap.set(this.mobileMenuDropdown, {
                opacity: 0,
                clipPath: 'inset(0 0 100% 0)', // Clip from bottom
                overflow: 'hidden'
            });
        }

        // Set initial states for menu items
        const menuItems = this.mobileMenuDropdown?.querySelectorAll('.mobile-nav-item');
        if (menuItems && menuItems.length > 0) {
            gsap.set(menuItems, {
                opacity: 0,
                y: 30,
                scale: 0.9
            });
        }

        // Set initial states for full-width gradient buttons (hide them until properly sized)
        const fullWidthButtons = this.mobileMenuDropdown?.querySelectorAll('.gradient-button[data-full-width="true"], .gradient-button-svg[data-full-width="true"]');
        if (fullWidthButtons && fullWidthButtons.length > 0) {
            fullWidthButtons.forEach(button => {
                const actualButton = button.classList.contains('gradient-button') ? button : button.closest('.gradient-button');
                if (actualButton) {
                    gsap.set(actualButton, { opacity: 0 });
                }
            });
        }

        // Top bar is now positioned absolutely and doesn't need initial GSAP states
    }

    bindEvents() {
        // Mobile menu toggle
        if (this.mobileMenuTrigger) {
            this.mobileMenuTrigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggleMobileMenu();
            });
        }

        // Mobile search toggle
        if (this.mobileSearchTrigger) {
            this.mobileSearchTrigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggleMobileSearch();
            });
        }

        // Internal close button in dialog
        const internalCloseButton = document.querySelector('[data-mobile-menu-close]');
        if (internalCloseButton) {
            internalCloseButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.closeMobileMenu();
            });
        }

        // Close search when clicking outside (mobile menu is handled by dialog)
        document.addEventListener('click', (e) => {
            if (!this.mobileMenuContainer.contains(e.target)) {
                if (this.isMobileSearchOpen) {
                    this.closeMobileSearch();
                }
            }
        });

        // Handle escape key for search (mobile menu is handled by dialog)
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                if (this.isMobileSearchOpen) {
                    this.closeMobileSearch();
                }
            }
        });

        // Handle window resize
        window.addEventListener('resize', () => {
            this.handleResize();
        });

        // React to header logo size transitions (scroll state toggle)
        window.addEventListener('header:request-scroll-toggle', this.onHeaderRequestToggle);
        window.addEventListener('header:scrolled-toggle', this.onHeaderScrolledToggle);
    }

    handleResize() {
        // Debounce resize events
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(() => {
            this.updateHeightOnResize();
        }, 250);
    }

    updateHeightOnResize() {
        // Recalculate original height after resize
        if (!this.isMobileMenuOpen) {
            const wasSearchOpen = this.isMobileSearchOpen;

            // Temporarily hide search to measure base header height
            if (wasSearchOpen && this.mobileSearchDropdown) {
                gsap.set(this.mobileSearchDropdown, { display: 'none' });
            }

            // Reset to auto temporarily
            gsap.set(this.mobileMenuContainer, { height: 'auto' });

            // Force reflow
            this.mobileMenuContainer.offsetHeight;

            // Recalculate with defensive minimum
            const calculatedHeight = this.mobileMenuContainer.offsetHeight;
            const minimumHeight = 60;
            this.originalHeight = Math.max(calculatedHeight, minimumHeight);

            // Clear cached expanded height
            this.searchCalculatedHeight = null;

            if (wasSearchOpen && this.mobileSearchDropdown) {
                // Show search again and compute expanded height
                gsap.set(this.mobileSearchDropdown, { display: 'block' });
                // Force reflow and recalc
                this.mobileMenuContainer.offsetHeight;
                let newHeight = this.mobileMenuContainer.scrollHeight;
                const minimumExpandedHeight = this.originalHeight + 50;
                newHeight = Math.max(newHeight, minimumExpandedHeight);
                this.searchCalculatedHeight = newHeight;
                // Update container height immediately
                gsap.set(this.mobileMenuContainer, { height: newHeight + 'px' });
            } else {
                // Set back to calculated height
                gsap.set(this.mobileMenuContainer, { height: this.originalHeight + 'px' });
            }
        }
    }

    setupHeaderHeightObserver() {
        // Prefer observing the top bar or site logo for intrinsic size changes
        const target = this.mobileMenuContainer.querySelector('.top-bar') ||
            this.mobileMenuContainer.querySelector('.site-logo');
        if (!target || typeof ResizeObserver === 'undefined') return;

        this.topBarResizeObserver = new ResizeObserver(() => {
            if (this.isMobileMenuOpen) return;
            // Coalesce multiple resize events in a frame
            if (this.roRafId) cancelAnimationFrame(this.roRafId);
            this.roRafId = requestAnimationFrame(() => this.updateHeightOnResize());
        });
        this.topBarResizeObserver.observe(target);
    }

    setupSubmenus() {
        const submenuTriggers = document.querySelectorAll('[data-mobile-submenu-trigger]');

        submenuTriggers.forEach(trigger => {
            const submenuId = trigger.getAttribute('data-mobile-submenu-trigger');
            const submenu = document.getElementById(submenuId);

            if (submenu) {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.toggleSubmenu(trigger, submenu);
                });
            }
        });
    }

    toggleMobileMenu() {
        if (this.isMobileMenuOpen) {
            this.closeMobileMenu();
        } else {
            this.openMobileMenu();
        }
    }

    openMobileMenu() {
        // Close search if open
        if (this.isMobileSearchOpen) {
            this.closeMobileSearch();
        }

        // Kill any existing animation
        if (this.menuTimeline) {
            this.menuTimeline.kill();
        }

        // Lock body scroll
        this.lockBodyScroll();

        this.mobileMenuContainer.classList.add('mobile-menu-open');
        this.isMobileMenuOpen = true;
        document.body.classList.add('nav-overlay-open');

        if (this.mobileMenuTrigger) {
            this.mobileMenuTrigger.setAttribute('aria-expanded', 'true');
            // Switch icon to close
            this.switchMenuIcon('close');
        }

        if (this.mobileMenuDropdown) {
            // Open the dialog (this handles accessibility automatically)
            this.mobileMenuDropdown.showModal();

            // Update focusable elements after dialog is shown
            this.updateFocusableElements();

            // Calculate target height for dialog (full screen)
            const containerTargetHeight = window.innerHeight; // Full screen for container

            // Create animation timeline
            this.menuTimeline = gsap.timeline({
                onComplete: () => {
                    // Set overflow to auto and focus first element after animation completes
                    gsap.set(this.mobileMenuDropdown, { overflow: 'auto' });
                    this.firstFocusableElement?.focus();
                    
                    // Resize full-width buttons now that mobile menu is fully open
                    this.resizeFullWidthButtons();
                }
            });

            // Animate main container height (full screen)
            this.menuTimeline.to(this.mobileMenuContainer, {
                height: containerTargetHeight,
                duration: this.ANIMATION_CONFIG.CONTAINER_DURATION,
                ease: this.ANIMATION_CONFIG.CONTAINER_EASE
            }, 0);

            // Animate dialog container using clip-path for background effect
            this.menuTimeline.to(this.mobileMenuDropdown, {
                opacity: 1,
                clipPath: 'inset(0 0 0% 0)', // Reveal from bottom
                duration: this.ANIMATION_CONFIG.CONTAINER_DURATION * 0.8,
                ease: this.ANIMATION_CONFIG.CONTAINER_EASE
            }, 0.1); // Start slightly after container begins

            // Top bar is now positioned absolutely and stays visible

            // Animate menu items with stagger
            const menuItems = this.mobileMenuDropdown.querySelectorAll('.mobile-nav-item');
            if (menuItems.length > 0) {
                this.menuTimeline.to(menuItems, {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: this.ANIMATION_CONFIG.ITEM_DURATION,
                    ease: this.ANIMATION_CONFIG.ITEM_EASE,
                    stagger: this.ANIMATION_CONFIG.STAGGER_DELAY
                }, 0.3); // Start after container animations begin
            }
        }
    }

    closeMobileMenu() {
        // Kill any existing animation
        if (this.menuTimeline) {
            this.menuTimeline.kill();
        }

        // Unlock body scroll
        this.unlockBodyScroll();

        this.isMobileMenuOpen = false;

        if (this.mobileMenuTrigger) {
            this.mobileMenuTrigger.setAttribute('aria-expanded', 'false');
            // Switch icon back to menu (hamburger)
            this.switchMenuIcon('menu');
        }

        if (this.mobileMenuDropdown) {
            // Create closing animation
            this.menuTimeline = gsap.timeline({
                onComplete: () => {
                    // Close the dialog (this handles accessibility and focus restoration automatically)
                    this.mobileMenuDropdown.close();

                    // Clean up after animation
                    this.mobileMenuContainer.classList.remove('mobile-menu-open');
                    document.body.classList.remove('nav-overlay-open');
                    gsap.set(this.mobileMenuDropdown, {
                        overflow: 'hidden',
                        opacity: 0,
                        clipPath: 'inset(0 0 100% 0)'
                    });

                    // Reset container to original height
                    gsap.set(this.mobileMenuContainer, {
                        height: this.originalHeight
                    });

                    // Reset menu items to initial state
                    const menuItems = this.mobileMenuDropdown.querySelectorAll('.mobile-nav-item');
                    gsap.set(menuItems, {
                        opacity: 0,
                        y: 30,
                        scale: 0.9
                    });

                    // Reset full-width buttons to hidden state
                    const fullWidthButtons = this.mobileMenuDropdown.querySelectorAll('.gradient-button[data-full-width="true"], .gradient-button-svg[data-full-width="true"]');
                    fullWidthButtons.forEach(button => {
                        const actualButton = button.classList.contains('gradient-button') ? button : button.closest('.gradient-button');
                        if (actualButton) {
                            gsap.set(actualButton, { opacity: 0 });
                        }
                    });

                    // Top bar is positioned absolutely and doesn't need reset

                    // Return focus to the trigger button
                    this.mobileMenuTrigger?.focus();
                }
            });

            // Animate menu items out first (reverse stagger)
            const menuItems = this.mobileMenuDropdown.querySelectorAll('.mobile-nav-item');
            if (menuItems.length > 0) {
                this.menuTimeline.to(menuItems, {
                    opacity: 0,
                    y: -20,
                    scale: 0.9,
                    duration: this.ANIMATION_CONFIG.CLOSE_DURATION * 0.6,
                    ease: this.ANIMATION_CONFIG.CLOSE_EASE,
                    stagger: -0.05 // Negative stagger for reverse order
                }, 0);
            }

            // Animate dropdown container
            this.menuTimeline.to(this.mobileMenuDropdown, {
                opacity: 0,
                clipPath: 'inset(0 0 100% 0)', // Hide to bottom
                duration: this.ANIMATION_CONFIG.CLOSE_DURATION * 0.8,
                ease: this.ANIMATION_CONFIG.CLOSE_EASE
            }, 0.1);

            // Finally animate main container back to original height
            this.menuTimeline.to(this.mobileMenuContainer, {
                height: this.originalHeight,
                duration: this.ANIMATION_CONFIG.CLOSE_DURATION,
                ease: this.ANIMATION_CONFIG.CLOSE_EASE
            }, 0.1);
        }

        // Close all open submenus
        this.closeAllSubmenus();
    }

    toggleMobileSearch() {
        if (this.isMobileSearchOpen) {
            this.closeMobileSearch();
        } else {
            this.openMobileSearch();
        }
    }

    openMobileSearch() {
        // Close menu if open
        if (this.isMobileMenuOpen) {
            this.closeMobileMenu();
        }

        if (!this.mobileSearchDropdown) return;

        // Kill any existing animation
        if (this.searchTimeline) {
            this.searchTimeline.kill();
            this.searchTimeline = null;
        }

        this.mobileMenuContainer.classList.add('mobile-search-open');
        this.isMobileSearchOpen = true;
        document.body.classList.add('nav-overlay-open');

        if (this.mobileSearchTrigger) {
            this.mobileSearchTrigger.setAttribute('aria-expanded', 'true');
        }

        // Prepare elements for measurement
        gsap.set(this.mobileMenuContainer, { height: this.originalHeight + 'px' });
        gsap.set(this.mobileSearchDropdown, { display: 'block', opacity: 0 });

        // Calculate or use cached height
        let expandedHeight;
        if (this.searchCalculatedHeight) {
            expandedHeight = this.searchCalculatedHeight;
        } else {
            expandedHeight = this.mobileMenuContainer.scrollHeight;
            const minimumExpandedHeight = this.originalHeight + 50;
            expandedHeight = Math.max(expandedHeight, minimumExpandedHeight);
            this.searchCalculatedHeight = expandedHeight;
        }

        // ARIA
        this.mobileSearchDropdown.removeAttribute('inert');
        this.mobileSearchDropdown.setAttribute('aria-hidden', 'false');

        // Animate
        this.searchTimeline = gsap.timeline({
            onComplete: () => {
                this.searchTimeline = null;
                // Focus the search input
                const searchInput = this.mobileSearchDropdown.querySelector('input[type="search"]') || this.mobileSearchDropdown.querySelector('.search-field');
                if (searchInput) {
                    searchInput.focus();
                }
            }
        });
        this.searchTimeline.to(this.mobileMenuContainer, {
            height: expandedHeight,
            duration: this.ANIMATION_CONFIG.CONTAINER_DURATION,
            ease: this.ANIMATION_CONFIG.CONTAINER_EASE
        }, 0);
        this.searchTimeline.to(this.mobileSearchDropdown, {
            opacity: 1,
            duration: this.ANIMATION_CONFIG.CONTAINER_DURATION * 0.6,
            ease: this.ANIMATION_CONFIG.CONTAINER_EASE
        }, 0.1);
    }

    closeMobileSearch() {
        if (!this.mobileSearchDropdown) return;

        // Kill any existing animation
        if (this.searchTimeline) {
            this.searchTimeline.kill();
            this.searchTimeline = null;
        }

        this.isMobileSearchOpen = false;
        document.body.classList.remove('nav-overlay-open');

        if (this.mobileSearchTrigger) {
            this.mobileSearchTrigger.setAttribute('aria-expanded', 'false');
        }

        // Animate out
        this.searchTimeline = gsap.timeline({
            onComplete: () => {
                this.mobileMenuContainer.classList.remove('mobile-search-open');
                gsap.set(this.mobileSearchDropdown, { display: 'none' });
                this.mobileSearchDropdown.setAttribute('inert', '');
                this.mobileSearchDropdown.setAttribute('aria-hidden', 'true');
                // Reset container height back to measured original
                gsap.set(this.mobileMenuContainer, { height: this.originalHeight + 'px' });
            }
        });
        this.searchTimeline.to(this.mobileSearchDropdown, {
            opacity: 0,
            duration: this.ANIMATION_CONFIG.CLOSE_DURATION * 0.6,
            ease: this.ANIMATION_CONFIG.CLOSE_EASE
        }, 0);
        this.searchTimeline.to(this.mobileMenuContainer, {
            height: this.originalHeight,
            duration: this.ANIMATION_CONFIG.CLOSE_DURATION,
            ease: this.ANIMATION_CONFIG.CLOSE_EASE
        }, 0.05);
    }

    toggleSubmenu(trigger, submenu) {
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
            // Close submenu
            trigger.setAttribute('aria-expanded', 'false');
            submenu.classList.remove('expanded');
            submenu.setAttribute('aria-hidden', 'true');
            submenu.setAttribute('inert', '');

            // Remove tabindex from submenu links to prevent focus
            const submenuLinks = submenu.querySelectorAll('a');
            submenuLinks.forEach(link => {
                link.setAttribute('tabindex', '-1');
            });
        } else {
            // Close other open submenus first
            this.closeAllSubmenus();

            // Open this submenu
            trigger.setAttribute('aria-expanded', 'true');
            submenu.classList.add('expanded');
            submenu.setAttribute('aria-hidden', 'false');
            submenu.removeAttribute('inert');

            // Restore tabindex for submenu links to allow focus
            const submenuLinks = submenu.querySelectorAll('a');
            submenuLinks.forEach(link => {
                link.removeAttribute('tabindex');
            });
        }
    }

    closeAllSubmenus() {
        const openSubmenus = document.querySelectorAll('.mobile-submenu.expanded');
        const openTriggers = document.querySelectorAll('[data-mobile-submenu-trigger][aria-expanded="true"]');

        openSubmenus.forEach(submenu => {
            submenu.classList.remove('expanded');
            submenu.setAttribute('aria-hidden', 'true');
            submenu.setAttribute('inert', '');

            // Remove tabindex from submenu links to prevent focus
            const submenuLinks = submenu.querySelectorAll('a');
            submenuLinks.forEach(link => {
                link.setAttribute('tabindex', '-1');
            });
        });

        openTriggers.forEach(trigger => {
            trigger.setAttribute('aria-expanded', 'false');
        });
    }

    /**
     * Resize full-width gradient buttons when mobile menu opens
     */
    resizeFullWidthButtons() {
        // Find all full-width gradient buttons in the mobile menu
        const fullWidthButtons = this.mobileMenuDropdown?.querySelectorAll('.gradient-button-svg[data-full-width="true"]');
        
        if (!fullWidthButtons || fullWidthButtons.length === 0) return;

        fullWidthButtons.forEach(svg => {
            const button = svg.closest('.gradient-button');
            const text = svg.querySelector('text');
            const rect = svg.querySelector('rect');
            
            if (!button || !text || !rect) return;

            // Get parent container width
            const parent = button.parentElement;
            let containerWidth = 0;
            
            if (parent) {
                containerWidth = parent.offsetWidth || parent.getBoundingClientRect().width;
            }
            
            // Fallback if still no width
            if (containerWidth === 0) {
                containerWidth = 280;
            }
            
            const svgHeight = 52;

            // Update SVG dimensions
            svg.setAttribute('width', containerWidth);
            svg.setAttribute('height', svgHeight);
            rect.setAttribute('width', containerWidth - 3);
            rect.setAttribute('height', svgHeight - 3);
            text.setAttribute('x', containerWidth / 2);
            text.setAttribute('y', svgHeight / 2 + 6);
            
            // Animate button to visible after resizing
            gsap.to(button, {
                opacity: 1,
                duration: 0.3,
                ease: "power2.out"
            });
        });
    }

    /**
     * Switch the mobile menu button icon
     */
    switchMenuIcon(iconName) {
        if (!this.mobileMenuTrigger) return;

        const iconContainer = this.mobileMenuTrigger.querySelector('.icon-container svg');
        if (!iconContainer) return;

        // Add transition class and fade out
        iconContainer.style.opacity = '0';

        setTimeout(() => {
            // Define icon content based on icon name
            let iconContent = '';

            if (iconName === 'menu') {
                // Hamburger menu icon paths
                iconContent = `
                    <path d="M2.25 9H15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/>
                    <path d="M2.25 4.5H15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/>
                    <path d="M2.25 13.5H15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/>
                `;
            } else if (iconName === 'x') {
                // Close (X) icon paths
                iconContent = `
                    <path d="M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="m6 6 12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                `;

                // Adjust viewBox for the X icon which is 24x24
                iconContainer.setAttribute('viewBox', '0 0 24 24');
            } else if (iconName === 'close') {
                // Close icon paths (using close.svg content)
                iconContent = `
                    <path d="M13.5 4.5L4.5 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/>
                    <path d="M4.5 4.5L13.5 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/>
                `;

                // Use 18x18 viewBox to match close.svg
                iconContainer.setAttribute('viewBox', '0 0 18 18');
            } else {
                // Default back to menu if unknown icon
                iconContent = `
                    <path d="M2.25 9H15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/>
                    <path d="M2.25 4.5H15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/>
                    <path d="M2.25 13.5H15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/>
                `;
                iconContainer.setAttribute('viewBox', '0 0 18 18');
            }

            // Set the appropriate viewBox
            if (iconName === 'menu') {
                iconContainer.setAttribute('viewBox', '0 0 18 18');
            }

            // Update the icon content
            iconContainer.innerHTML = iconContent;

            // Fade back in
            iconContainer.style.opacity = '1';
        }, 150); // Half of the CSS transition duration
    }

    /**
     * Lock body scroll and save current position
     */
    lockBodyScroll() {
        // Save current scroll position
        this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

        // Add class to body to lock scrolling
        document.body.classList.add('mobile-menu-open');
    }

    /**
     * Unlock body scroll and restore position
     */
    unlockBodyScroll() {
        // Remove class from body
        document.body.classList.remove('mobile-menu-open');

        // Restore scroll position
        window.scrollTo(0, this.scrollPosition);
    }

    /**
     * Clean up event listeners and references
     */
    destroy() {
        // Kill any active animations
        if (this.menuTimeline) {
            this.menuTimeline.kill();
            this.menuTimeline = null;
        }
        if (this.searchTimeline) {
            this.searchTimeline.kill();
            this.searchTimeline = null;
        }

        // Ensure body scroll is unlocked
        if (this.isMobileMenuOpen) {
            this.unlockBodyScroll();
        }

        // Remove event listeners
        if (this.mobileMenuTrigger) {
            this.mobileMenuTrigger.removeEventListener('click', this.toggleMobileMenu);
        }
        if (this.mobileSearchTrigger) {
            this.mobileSearchTrigger.removeEventListener('click', this.toggleMobileSearch);
        }

        document.removeEventListener('click', this.handleOutsideClick);
        document.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('header:request-scroll-toggle', this.onHeaderRequestToggle);
        window.removeEventListener('header:scrolled-toggle', this.onHeaderScrolledToggle);
        if (this.topBarResizeObserver) {
            try { this.topBarResizeObserver.disconnect(); } catch (e) { }
            this.topBarResizeObserver = null;
        }

        // Clear references
        this.mobileMenuContainer = null;
        this.mobileMenuTrigger = null;
        this.mobileMenuDropdown = null;
        this.mobileSearchTrigger = null;
        this.mobileSearchDropdown = null;
    }
}